import type { IdentityProvider } from "./session.js";

/**
 * Sign-in with Google (issue 119, PRD M6, research-identity §1.5).
 *
 * Google Identity Services draws the button and hands back a Google ID token for this OAuth
 * client; one Identity Toolkit REST call, `accounts:signInWithIdp`, turns it into an Identity
 * Platform ID token, which goes once to `POST /api/auth/session` (`session.ts`) and is dropped.
 * No `firebase` package, no popup or redirect through `firebaseapp.com`: `forbid.toml`'s
 * `[[npm]]` bans pin the package half.
 */

/**
 * The OAuth client (PRD, "Human prerequisites"). Public: GIS puts it in the page anyway.
 * `infra/terraform.tfvars`'s `google_oauth_client_id` is the same value, and
 * `tests/google-client-id.test.ts` holds the two together.
 */
export const GOOGLE_CLIENT_ID =
  "270637213952-dkl4k5fb75bab13ulkim51epjbnaj10j.apps.googleusercontent.com";

/** GIS is loaded from Google by script tag; a self-hosted copy is not supported (research §1.4). */
export const GIS_SCRIPT = "https://accounts.google.com/gsi/client";

const SIGN_IN_WITH_IDP = "https://identitytoolkit.googleapis.com/v1/accounts:signInWithIdp";

/** A key that reaches nothing, so a development build draws the button for stubbed specs. */
const DEV_PLACEHOLDER_KEY = "development-build-has-no-browser-key";

export interface GoogleConfig {
  readonly clientId: string;
  /** The Console's browser key: Identity Toolkit, three methods, the Console's referrer. */
  readonly apiKey: string;
  /** The page's origin, which `signInWithIdp` takes as `requestUri`. */
  readonly requestUri: string;
}

/**
 * The build's Google configuration, or `null` for a build that cannot sign in with Google.
 *
 * The browser key is a build input (`VITE_IDENTITY_API_KEY`, set by the Dockerfile from
 * `cargo xtask deploy`) rather than a string in the repository. A production build without
 * one draws no Google button, and the sign-in screen says no method is available.
 */
export function googleConfig(
  env: { readonly DEV: boolean; readonly VITE_IDENTITY_API_KEY?: string },
  origin: string,
): GoogleConfig | null {
  const key = env.VITE_IDENTITY_API_KEY ?? "";
  if (key === "" && !env.DEV) return null;
  return {
    clientId: GOOGLE_CLIENT_ID,
    apiKey: key === "" ? DEV_PLACEHOLDER_KEY : key,
    requestUri: origin,
  };
}

/**
 * The address already has an account that Google is not trusted to claim (`needConfirmation`,
 * research-identity §3). No account was created and nothing reaches the server.
 */
export class AccountExistsWithDifferentCredential extends Error {
  constructor() {
    super("account-exists-with-different-credential");
    this.name = "AccountExistsWithDifferentCredential";
  }
}

/** The two `signInWithIdp` messages that mean the same case as `needConfirmation`. */
const ACCOUNT_EXISTS = ["EMAIL_EXISTS", "FEDERATED_USER_ID_ALREADY_LINKED"];

function isAccountExists(message: unknown): boolean {
  return (
    typeof message === "string" &&
    ACCOUNT_EXISTS.some((code) => message === code || message.startsWith(`${code} `))
  );
}

/**
 * Exchange a GIS credential for an Identity Platform ID token.
 *
 * Rejects with {@link AccountExistsWithDifferentCredential} for the account-exists case, and
 * with a plain `Error` naming only the status for anything else: a refusal here is a fault
 * (a key, a referrer, a disabled provider), never "not invited", which only the exchange
 * decides.
 */
export async function signInWithIdp(
  fetchImpl: typeof fetch,
  config: GoogleConfig,
  credential: string,
): Promise<string> {
  const response = await fetchImpl(`${SIGN_IN_WITH_IDP}?key=${encodeURIComponent(config.apiKey)}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      requestUri: config.requestUri,
      postBody: `id_token=${encodeURIComponent(credential)}&providerId=google.com`,
      returnSecureToken: true,
    }),
  });
  const body = (await response.json().catch(() => ({}))) as {
    idToken?: unknown;
    needConfirmation?: unknown;
    errorMessage?: unknown;
    error?: { message?: unknown };
  };
  if (
    body.needConfirmation === true ||
    isAccountExists(body.errorMessage) ||
    isAccountExists(body.error?.message)
  ) {
    throw new AccountExistsWithDifferentCredential();
  }
  if (!response.ok) throw new Error(`signInWithIdp answered ${response.status}`);
  if (typeof body.idToken !== "string" || body.idToken === "") {
    throw new Error("signInWithIdp answered no ID token");
  }
  return body.idToken;
}

/** One completed Google button press, as the sign-in screen's provider. */
export function googleProvider(
  config: GoogleConfig,
  credential: string,
  fetchImpl: typeof fetch = fetch,
): IdentityProvider {
  return {
    label: "Sign in with Google",
    idToken: () => signInWithIdp(fetchImpl, config, credential),
  };
}

// ---------------------------------------------------------------------------
// Google Identity Services, the script
// ---------------------------------------------------------------------------

/** The part of `google.accounts.id` the Console calls. */
export interface GoogleAccountsId {
  initialize(options: {
    client_id: string;
    callback: (response: { credential: string }) => void;
    ux_mode?: "popup" | "redirect";
    auto_select?: boolean;
  }): void;
  renderButton(parent: HTMLElement, options: Record<string, string | number>): void;
}

declare global {
  interface Window {
    google?: { accounts?: { id?: GoogleAccountsId } };
  }
}

let loading: Promise<GoogleAccountsId> | null = null;

/** Load GIS once per page. A failed load is forgotten, so the next mount tries again. */
export function loadGoogleIdentity(): Promise<GoogleAccountsId> {
  const ready = window.google?.accounts?.id;
  if (ready) return Promise.resolve(ready);
  loading ??= new Promise<GoogleAccountsId>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = GIS_SCRIPT;
    script.async = true;
    script.onload = () => {
      const id = window.google?.accounts?.id;
      if (id) resolve(id);
      else reject(new Error("Google Identity Services loaded without google.accounts.id"));
    };
    script.onerror = () => reject(new Error("Google Identity Services did not load"));
    document.head.appendChild(script);
  }).catch((error: unknown) => {
    loading = null;
    throw error;
  });
  return loading;
}
